import { useEffect, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { ShieldAlert, CheckCircle2, XCircle, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function GuardianApprovePage() {
  const { token } = useParams();
  const { search } = useLocation();
  // ?kind=haunted routes the fetch/approve to the Haunted House waiver endpoints
  const isHaunted = new URLSearchParams(search).get('kind') === 'haunted';
  const fetchUrl = isHaunted
    ? `${API}/api/hh/waiver/guardian/${token}`
    : `${API}/api/waiver/guardian/${token}`;
  const approveUrl = isHaunted
    ? `${API}/api/hh/waiver/guardian/${token}/approve`
    : `${API}/api/waiver/guardian/${token}/approve`;
  const [state, setState] = useState({ loading: true, data: null, err: '' });
  const [name, setName] = useState('');
  const [signature, setSignature] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    axios.get(fetchUrl)
      .then(r => setState({ loading: false, data: r.data, err: '' }))
      .catch(e => setState({ loading: false, data: null, err: e?.response?.data?.detail || 'Link invalid or expired' }));
  }, [token, fetchUrl]);

  const submit = async (approved) => {
    if (!name.trim() || !signature.trim()) {
      setErr('Please type your name and signature');
      return;
    }
    setBusy(true); setErr('');
    try {
      const r = await axios.post(approveUrl,
        { guardian_name: name.trim(), approved, signature_typed: signature.trim() });
      setDone({ approved, status: r.data.status });
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Something went wrong');
    } finally { setBusy(false); }
  };

  if (state.loading) {
    return <div className="min-h-screen bg-black text-white flex items-center justify-center">
      <Loader2 className="animate-spin" size={40} />
    </div>;
  }
  if (state.err) {
    return <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center p-6 text-center">
      <XCircle size={48} className="text-red-500 mb-4" />
      <p className="font-heading text-lg tracking-widest uppercase">{state.err}</p>
    </div>;
  }
  const d = state.data;
  const alreadyResponded = d.guardian_status && d.guardian_status !== 'pending';

  return (
    <div className="min-h-screen bg-neutral-950 text-white p-4 sm:p-6">
      <div className="max-w-2xl mx-auto space-y-4">
        <div className="rounded-2xl overflow-hidden border-2 border-orange-500/50" style={{ boxShadow: '0 0 30px rgba(255,120,20,0.35)' }}>
          <div className="bg-gradient-to-r from-red-700 via-orange-700 to-red-800 p-5 flex items-center gap-2">
            <ShieldAlert size={22} className="text-white" />
            <h1 className="font-heading text-lg sm:text-xl tracking-widest uppercase text-white">Guardian Approval</h1>
          </div>

          <div className="p-5 space-y-4">
            <div className="text-sm space-y-1">
              <p><b className="text-orange-300">{d.signer_name || 'A friend or family member'}</b> submitted a waiver bringing:</p>
              <p className="font-heading text-2xl tracking-wide" data-testid="minor-name">{d.minor_name}</p>
              <p className="text-xs text-muted-foreground">to an OKI-ESCAPE experience. Because they are under 18, we need a parent or legal guardian to approve.</p>
            </div>

            {done ? (
              <div className="rounded-lg border p-4 text-center space-y-2"
                style={{ borderColor: done.approved ? 'rgba(16,185,129,0.5)' : 'rgba(239,68,68,0.5)',
                         background: done.approved ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)' }}>
                {done.approved
                  ? <CheckCircle2 size={42} className="text-emerald-400 mx-auto" />
                  : <XCircle size={42} className="text-red-400 mx-auto" />}
                <p className="font-heading text-lg tracking-widest uppercase">
                  {done.approved ? 'Approved' : 'Declined'}
                </p>
                <p className="text-xs text-muted-foreground">
                  {done.approved
                    ? 'Thank you. The waiver is now valid for their visit.'
                    : 'We have recorded your decline. Your child will not be allowed to participate.'}
                </p>
              </div>
            ) : alreadyResponded ? (
              <div className="rounded-lg border border-blue-500/50 bg-blue-950/30 p-4 text-center">
                <p className="font-heading text-sm tracking-widest uppercase text-blue-300">Already responded: {d.guardian_status}</p>
                {d.responded_at && <p className="text-xs text-muted-foreground mt-1">on {new Date(d.responded_at).toLocaleString()}</p>}
              </div>
            ) : (
              <>
                <details className="rounded-lg border border-neutral-700 bg-black/40" data-testid="guardian-legal">
                  <summary className="font-heading text-[10px] tracking-widest uppercase text-orange-400 cursor-pointer p-3">Read the full waiver</summary>
                  <div className="p-3 pt-0 text-xs whitespace-pre-line max-h-64 overflow-y-auto">{d.legal_text_en}</div>
                </details>

                <div className="space-y-2">
                  <label className="font-heading text-[10px] tracking-widest uppercase text-muted-foreground">Your legal name (parent / guardian)</label>
                  <input value={name} onChange={e => setName(e.target.value)} placeholder="Parent full name"
                    data-testid="guardian-name"
                    className="w-full bg-black border border-neutral-700 focus:border-orange-500 rounded-lg px-3 py-2 text-sm outline-none" />
                  <label className="font-heading text-[10px] tracking-widest uppercase text-muted-foreground">Type your signature</label>
                  <input value={signature} onChange={e => setSignature(e.target.value)} placeholder="Type your name as signature"
                    data-testid="guardian-signature"
                    className="w-full bg-black border border-neutral-700 focus:border-orange-500 rounded-lg px-3 py-2 text-sm outline-none font-heading tracking-wide italic" />
                </div>

                {err && <p className="text-xs text-red-400 text-center">{err}</p>}

                <div className="grid grid-cols-2 gap-3">
                  <button onClick={() => submit(false)} disabled={busy} data-testid="guardian-decline"
                    className="bg-neutral-900 border border-red-500/50 hover:border-red-400 text-red-300 font-heading tracking-widest uppercase text-sm py-3 rounded-full disabled:opacity-40">
                    Decline
                  </button>
                  <button onClick={() => submit(true)} disabled={busy} data-testid="guardian-approve"
                    className="bg-emerald-600 hover:bg-emerald-500 text-black font-heading tracking-widest uppercase text-sm py-3 rounded-full disabled:opacity-40 inline-flex items-center justify-center gap-2">
                    {busy ? <Loader2 className="animate-spin" size={14} /> : <CheckCircle2 size={14} />}
                    Approve
                  </button>
                </div>
              </>
            )}

            <p className="text-[10px] text-neutral-500 text-center pt-2">Waiver ID {d.waiver_id}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
